// @flow
import shortId from 'shortid';
import { get } from 'lodash';
import { pipe, map, path, flatten, compact, uniqBy } from 'lodash/fp';
import { ICONS } from 'Components/utils/icon/icons';
import { CABIN_TYPE, ROOM_TYPE } from 'Utils/accommodation/accommodation.constants';
import { tAlt } from 'Components/utils/booking/bookingUtils';

type Category = {
  id: string,
};

type Strength = {
  id: string,
  label: string,
};

const COMFORT_CATEGORY = 'accommodations';
const EXTRA_FEATURES_CATEGORY = 'extra';
const CONFIGURATION_CATEGORY = 'configuration';
const REDUCED_MOBILITY_FILTER_ID = 'reduced_mobility_access';

const matchConfiguration = (accommodations: Array<Accommodation>, roomType: string): boolean => {
  return accommodations.some((accommodation: Accommodation) => accommodation.roomType === roomType);
};

const matchComfort = (accommodations: Array<Accommodation>, categoryId: string): boolean => {
  return accommodations.some((accommodation: Accommodation) =>
    get(accommodation, 'categories').find(
      (category: Category) => get(category, 'id') === categoryId,
    ),
  );
};

const matchExtraFeatures = (accommodations: Array<Accommodation>, strengthId: string): boolean => {
  return accommodations.some(
    (accommodation: Accommodation) =>
      get(accommodation, 'view.id', null) === strengthId ||
      get(accommodation, 'strengths', []).find(
        (strength: Strength) => get(strength, 'id') === strengthId,
      ),
  );
};

const matchExtraFeaturesReducedMobility = (accommodations: Array<Accommodation>): boolean => {
  return accommodations.some((accommodation: Accommodation) => accommodation.reducedMobilityAccess);
};

function createConfigurationFilters(
  accommodationsSets: Array<AccommodationsSet>,
  resort: Resort,
): Array<Filter> {
  const accommodations = pipe([map(path('accommodations')), flatten, uniqBy('roomType')])(
    accommodationsSets,
  );

  return accommodations.map(({ roomType }) => ({
    id: shortId.generate(),
    category: CONFIGURATION_CATEGORY,
    match: matchConfiguration,
    className: 'additionalAccommodationsFilters',
    meta: {
      label: tAlt({
        resortKeyPath: `filters.configuration.${ROOM_TYPE[roomType]}`,
        cruiseKeyPath: `filters.configuration.${CABIN_TYPE[roomType]}`,
        resort,
      }),
      icon: ICONS[roomType],
      iconClassName: `icon--${roomType}`,
      iconInheritColor: true,
    },
    path: 'accommodations',
    selected: false,
    value: roomType,
  }));
}

function createComfortFilters(accommodationsSets: Array<AccommodationsSet>): Array<Filter> {
  const accommodations = pipe([
    map(path('accommodations')),
    flatten,
    compact,
    uniqBy('categoryId'),
  ])(accommodationsSets);

  const comfortSeen = [];
  return accommodations.map(accommodation => {
    /**
     * in some cases we have differents categories with same comfort type
     * it causes a duplicate filter
     * Concat comfort type & category, as label for teh second one
     */
    const { comfortType, category } = accommodation;
    const label = comfortSeen.includes(comfortType)
      ? `${comfortType} ${category}`
      : comfortType || category;

    comfortSeen.push(accommodation.comfortType);

    return {
      id: shortId.generate(),
      category: COMFORT_CATEGORY,
      match: matchComfort,
      className: 'additionalAccommodationsFilters',
      meta: {
        label,
      },
      path: 'accommodations',
      selected: false,
      value: accommodation.categoryId,
    };
  });
}

function createExtraFeaturesFilters(
  accommodationsSets: Array<AccommodationsSet>,
  i18n: *,
): Array<Filter> {
  const accommodations = pipe([map(path('accommodations')), flatten, uniqBy('id')])(
    accommodationsSets,
  );

  const strengths = pipe([map(path('strengths')), flatten, compact, uniqBy('id')])(accommodations);

  const views = pipe([map(path('view')), compact, uniqBy('id')])(accommodations);

  const reducedMobityAccess = accommodations.some(
    accommodation => accommodation.reducedMobilityAccess,
  )
    ? [
        {
          id: REDUCED_MOBILITY_FILTER_ID,
          label: i18n.filters.reducedMobility,
        },
      ]
    : [];

  const features = [...strengths, ...views, ...reducedMobityAccess];

  return features.map(feature => ({
    id: shortId.generate(),
    category: EXTRA_FEATURES_CATEGORY,
    match:
      feature.id === REDUCED_MOBILITY_FILTER_ID
        ? matchExtraFeaturesReducedMobility
        : matchExtraFeatures,
    meta: {
      label: feature.label,
      icon: ICONS[feature.id],
      iconClassName: `icon--${feature.id}`,
      iconInheritColor: true,
    },
    path: 'accommodations',
    selected: false,
    value: feature.id,
  }));
}

function createFilters(
  accommodationsSets: Array<AccommodationsSet>,
  i18n: *,
  resort: Resort,
): Array<Filter> {
  return [
    ...createExtraFeaturesFilters(accommodationsSets, i18n),
    ...createConfigurationFilters(accommodationsSets, resort),
    ...createComfortFilters(accommodationsSets),
  ];
}

function unselectCategory(filters: Array<Filter>): string => Array<Filter> {
  return category => {
    return filters.map(filter => ({
      ...filter,
      selected: filter.category === category ? false : filter.selected,
    }));
  };
}

export {
  COMFORT_CATEGORY,
  EXTRA_FEATURES_CATEGORY,
  CONFIGURATION_CATEGORY,
  matchConfiguration,
  matchComfort,
  matchExtraFeatures,
  createFilters,
  createConfigurationFilters,
  createComfortFilters,
  createExtraFeaturesFilters,
  unselectCategory,
  REDUCED_MOBILITY_FILTER_ID,
  matchExtraFeaturesReducedMobility,
};
